import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router-dom';
import Login from '../login/Login';
import { useSession } from '../../contexts/session-context';
import { HUB_LOGIN_PATH, HUB_REGISTER_PATH } from '../../routes/paths';
import {
    clearSsoReturnOutcome,
    getSsoReturnOutcome,
    takeSsoResumePath
} from '../../services/google-sso';
import { postLoginDestination } from '../login/post-login-destination';

export default function HubLoginPage() {
    const { authorize, status, isAuthenticated, user } = useSession();
    const redirected = useRef(false);
    // UX-802: the Google return outcome, banked by `hub-app.tsx` before the router existed — only a
    // return that landed on THIS page. Read once into state (a pure getter, so StrictMode's double
    // initializer call is harmless) and then cleared, so navigating back to this page later in the
    // same document does not replay it.
    const [ssoOutcome] = useState(() => getSsoReturnOutcome(HUB_LOGIN_PATH));
    const isNoAccount = ssoOutcome === 'no-account';
    // The form's own sign-in succeeded: keep showing the form (its button holds a spinner) until the
    // redirect lands, instead of replacing it with the full-page spinner below.
    const [signedInViaForm, setSignedInViaForm] = useState(false);

    useEffect(() => {
        clearSsoReturnOutcome();
    }, []);

    /**
     * Where a Google sign-in started from the MCP consent screen goes back to. Taken (read and
     * removed) once, whatever the outcome, so a failed or abandoned round trip cannot redirect a
     * later, unrelated sign-in; followed only for a successful one.
     */
    const [resumePath] = useState(() =>
        ssoOutcome !== undefined ? takeSsoResumePath() : undefined
    );

    useEffect(() => {
        // A Google address with no matching account goes straight to registration; there is no
        // session to look for, and the register page runs its own authorize.
        if (isNoAccount) return;
        // Only check the session once. After a client-side navigation between hub pages it is
        // already resolved, and re-running `authorize` would flip `status` back to 'loading' and
        // swap the whole page for the bare spinner (a white flash).
        if (status !== 'idle') return;
        authorize();
    }, [authorize, isNoAccount, status]);

    useEffect(() => {
        if (!isAuthenticated || redirected.current) return;
        redirected.current = true;
        // ROBO-478: a suspended business's Owner lands on billing — this is a sign-in, whether the
        // session came from the form, the Google return or a visitor who already held one. A
        // refused authorize never gets here: it leaves `status` unauthenticated and the form below
        // shows the Account Inactive notice.
        //
        // A Google sign-in begun on the consent screen goes back there instead — the same choice the
        // password form makes there with `stayOnPage`, and for the same reason: the page handles
        // who may connect itself, and the connection request exists only in its URL.
        window.location.replace(
            ssoOutcome === 'success' && resumePath ? resumePath : postLoginDestination(user)
        );
    }, [isAuthenticated, user, ssoOutcome, resumePath]);

    if (isNoAccount) {
        return <Navigate to={HUB_REGISTER_PATH} replace />;
    }

    // `sso=success` (or a stray `created`) lands here too: the SessionKey it carried is already
    // persisted, so this spinner covers the authorize and the redirect — the login form (and its
    // error) never flashes.
    if (status === 'loading' || status === 'idle' || (isAuthenticated && !signedInViaForm)) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ height: '100vh', width: '100vw', overflow: 'hidden' }}
            >
                <CircularProgress color="inherit" size={32} sx={{ opacity: 0.25 }} />
            </Box>
        );
    }

    return <Login ssoOutcome={ssoOutcome} onSignedIn={() => setSignedInViaForm(true)} />;
}
